<template>
  <!-- ── Backdrop (floating + mobile modes) ────────────────────────────────── -->
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open && !effectivePinned" class="fixed inset-0 z-40 bg-black/30" @click="close()" />
    </Transition>
  </Teleport>

  <!-- ── Drawer panel ───────────────────────────────────────────────────────
       - Pinned (desktop): in-flow flex column, sticky, pushes main content left
       - Floating (desktop): position:fixed panel right side  
       - Mobile: position:fixed full-screen -->
  <Transition :name="effectivePinned ? 'drawer-push' : 'drawer-slide'">
    <div
      v-if="open"
      class="glass-surface flex flex-col bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 overflow-hidden pt-safe pb-safe"
      :class="[
        effectivePinned ? 'flex-shrink-0 h-screen sticky top-0 border-l shadow-lg' : 'fixed z-50 shadow-2xl border',
        !effectivePinned && !isMobile ? 'rounded-2xl' : '',
      ]"
      :style="panelStyle">
      <!-- ── Header ──────────────────────────────────────────────────────── -->
      <div
        class="glass-header flex items-center gap-2.5 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 flex-shrink-0 h-14">
        <!-- Avatar -->
        <div
          class="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 bg-cover bg-center flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
          :style="userImageUrl && !avatarError ? `background-image: url('${userImageUrl}')` : ''">
          <img v-if="userImageUrl && !avatarError" :src="userImageUrl" class="hidden" @error="avatarError = true" />
          <span v-if="!userImageUrl || avatarError" class="select-none">{{ userInitials }}</span>
        </div>
        <!-- Name + status -->
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-gray-900 dark:text-white truncate leading-tight">
            {{ authStore.user?.name || authStore.user?.email }}
          </p>
          <div class="flex items-center gap-1">
            <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="statusDotColor"></span>
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ statusLabel(peopleStore.myStatus, true) }}</span>
          </div>
        </div>
        <!-- Pin/unpin (desktop only) -->
        <button
          v-if="!isMobile"
          @click="ui.toggleUserDrawerPin()"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0 transition-colors"
          :title="effectivePinned ? tt('Unpin drawer', 'Odpni predal') : tt('Pin drawer', 'Pripni predal')">
          <span class="material-symbols-outlined text-lg">{{ effectivePinned ? 'push_pin' : 'keep' }}</span>
        </button>
        <!-- Close (floating + mobile) -->
        <button
          v-if="!effectivePinned"
          @click="close()"
          class="p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 flex-shrink-0">
          <span class="material-symbols-outlined text-lg">close</span>
        </button>
      </div>

      <!-- ── Tab bar ──────────────────────────────────────────────────────── -->
      <div class="glass-header flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <button
          v-for="tab in TABS"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'flex-1 pt-2 pb-1.5 text-[10px] font-medium transition-colors flex flex-col items-center justify-center gap-0.5 border-b-2 relative',
            activeTab === tab.id
              ? 'text-blue-600 dark:text-blue-400 border-blue-500'
              : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200',
          ]">
          <span class="relative">
            <span class="material-symbols-outlined" style="font-size: 20px">{{ tab.icon }}</span>
            <span
              v-if="(tab.badge ?? 0) > 0"
              class="absolute -top-1 -right-1.5 min-w-[14px] h-3.5 px-0.5 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none"
              :class="tab.badgeColor || 'bg-red-500'"
              >{{ (tab.badge ?? 0) > 99 ? '99+' : tab.badge }}</span
            >
          </span>
          <span class="leading-none">{{ tt(tab.label.en, tab.label.sl) }}</span>
        </button>
      </div>

      <!-- ── Tab content ──────────────────────────────────────────────────── -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- ══ ME TAB ══ -->
        <template v-if="activeTab === 'user'">
          <!-- Status -->
          <div class="px-3 pt-3 pb-2">
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-1.5">
              {{ tt('Status', 'Stanje') }}
            </p>
            <div class="flex flex-col gap-0.5">
              <button
                v-for="opt in STATUS_OPTIONS"
                :key="opt.value"
                @click="myStatus = opt.value"
                :class="[
                  'flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm transition-colors w-full text-left',
                  myStatus === opt.value
                    ? 'bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50',
                ]">
                <span :class="['material-symbols-outlined', opt.color]" style="font-size: 10px">circle</span>
                {{ tt(opt.label.en, opt.label.sl) }}
                <span v-if="myStatus === opt.value" class="material-symbols-outlined text-xs ml-auto text-blue-500"
                  >check</span
                >
              </button>
            </div>
          </div>

          <div class="glass-divider mx-4 border-t border-gray-100 dark:border-gray-700 my-1" />

          <!-- Alarms / Timers / Reminders -->
          <div class="px-3 py-2">
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-2">
              {{ tt('Active', 'Aktivno') }}
            </p>
            <div class="flex flex-col gap-1.5">
              <!-- Alarms -->
              <router-link
                :to="hubAlarmsPath"
                @click="closeIfFloating()"
                class="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                <div
                  class="p-1.5 rounded-lg flex-shrink-0"
                  :class="
                    alarmsStore.ringingAlarm ? 'bg-yellow-500 animate-pulse' : 'bg-yellow-100 dark:bg-yellow-900/30'
                  ">
                  <span
                    class="material-symbols-outlined text-sm"
                    :class="alarmsStore.ringingAlarm ? 'text-white' : 'text-yellow-600 dark:text-yellow-400'"
                    >alarm</span
                  >
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ tt('Alarms', 'Alarmi') }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{
                      alarmsStore.ringingAlarm
                        ? `${tt('Ringing', 'Zvoni')}: ${alarmsStore.ringingAlarm.label}`
                        : `${enabledAlarmsCount} ${tt('enabled', slCount(enabledAlarmsCount, 'omogočen', 'omogočena', 'omogočeni', 'omogočenih'))}`
                    }}
                  </p>
                </div>
                <span
                  class="material-symbols-outlined text-sm text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200"
                  >chevron_right</span
                >
              </router-link>

              <!-- Timers -->
              <router-link
                :to="hubTimersPath"
                @click="closeIfFloating()"
                class="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                <div
                  class="p-1.5 rounded-lg flex-shrink-0"
                  :class="runningTimersCount > 0 ? 'bg-blue-500' : 'bg-blue-100 dark:bg-blue-900/30'">
                  <span
                    class="material-symbols-outlined text-sm"
                    :class="runningTimersCount > 0 ? 'text-white' : 'text-blue-600 dark:text-blue-400'"
                    >timer</span
                  >
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ tt('Timers', 'Časovniki') }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{
                      runningTimersCount > 0
                        ? `${runningTimersCount} ${tt('running', 'v teku')}`
                        : tt('None running', 'Noben ne teče')
                    }}
                  </p>
                </div>
                <span
                  class="material-symbols-outlined text-sm text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200"
                  >chevron_right</span
                >
              </router-link>

              <!-- Reminders -->
              <router-link
                :to="hubRemindersPath"
                @click="closeIfFloating()"
                class="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                <div
                  class="p-1.5 rounded-lg flex-shrink-0"
                  :class="
                    remindersStore.recentlyDue
                      ? 'bg-purple-500 animate-pulse'
                      : upcomingRemindersCount > 0
                        ? 'bg-purple-100 dark:bg-purple-900/30'
                        : 'bg-gray-100 dark:bg-gray-700'
                  ">
                  <span
                    class="material-symbols-outlined text-sm"
                    :class="
                      remindersStore.recentlyDue
                        ? 'text-white'
                        : upcomingRemindersCount > 0
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-gray-500'
                    "
                    >task_alt</span
                  >
                </div>
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ tt('Reminders', 'Opomniki') }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{
                      remindersStore.recentlyDue
                        ? `${tt('Due', 'Zapade')}: ${remindersStore.recentlyDue.title}`
                        : `${upcomingRemindersCount} ${tt('upcoming', slCount(upcomingRemindersCount, 'prihaja', 'prihajata', 'prihajajo', 'prihajajočih'))}`
                    }}
                  </p>
                </div>
                <span
                  class="material-symbols-outlined text-sm text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200"
                  >chevron_right</span
                >
              </router-link>
            </div>
          </div>

          <div class="glass-divider mx-4 border-t border-gray-100 dark:border-gray-700 my-1" />

          <!-- Shortcuts: the pages that no longer sit in the main menu. -->
          <div class="px-3 py-2">
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-2">
              {{ tt('Shortcuts', 'Bližnjice') }}
            </p>
            <div class="grid grid-cols-3 gap-1.5">
              <router-link
                v-for="link in pageShortcuts"
                :key="link.id"
                :to="link.to"
                @click="closeIfFloating()"
                class="flex flex-col items-center gap-1 rounded-xl bg-gray-50 px-1 py-2.5 text-[10px] font-medium leading-none text-gray-600 transition-colors hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700">
                <span class="material-symbols-outlined text-lg">{{ link.icon }}</span>
                <span class="text-center">{{ tt(link.label.en, link.label.sl) }}</span>
              </router-link>
            </div>
          </div>

          <template v-if="!isViewer">
            <div class="glass-divider mx-4 border-t border-gray-100 dark:border-gray-700 my-1" />

            <!-- Account & Devices. A viewer has no account centre: the demo
                 guest's hub row is throwaway and has no cloud account behind
                 it (card t_803e86a4). -->
            <router-link
              to="/account"
              @click="closeIfFloating()"
              class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <span class="material-symbols-outlined text-base flex-shrink-0">manage_accounts</span>
              {{ tt('Account settings', 'Nastavitve računa') }}
              <span class="ml-auto material-symbols-outlined text-base text-gray-400">chevron_right</span>
            </router-link>

            <div class="glass-divider mx-4 border-t border-gray-100 dark:border-gray-700 my-1" />
          </template>

          <!-- Client devices -->
          <div class="px-3 pt-2 pb-1">
            <div class="flex items-center justify-between px-1 mb-1.5">
              <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                {{ tt('My Devices', 'Moje naprave') }}
              </p>
              <button
                @click="loadDevices()"
                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                :title="tt('Refresh', 'Osveži')">
                <span class="material-symbols-outlined text-sm" :class="devicesLoading ? 'animate-spin' : ''"
                  >refresh</span
                >
              </button>
            </div>
            <div v-if="devicesLoading" class="flex justify-center py-4">
              <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div
              v-else-if="devices.length === 0"
              class="flex flex-col items-center py-4 text-gray-400 dark:text-gray-500">
              <span class="material-symbols-outlined text-2xl">devices</span>
              <p class="text-xs mt-1">{{ tt('No devices registered', 'Ni registriranih naprav') }}</p>
            </div>
            <div v-else class="flex flex-col gap-0.5">
              <div
                v-for="device in devices"
                :key="device.id"
                class="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 group">
                <span class="material-symbols-outlined text-base text-gray-400 flex-shrink-0">
                  {{
                    device.platform === 'ios'
                      ? 'phone_iphone'
                      : device.platform === 'android'
                        ? 'phone_android'
                        : 'devices'
                  }}
                </span>
                <div class="min-w-0 flex-1">
                  <p class="text-sm text-gray-900 dark:text-white truncate">
                    {{ device.deviceName || device.platform || tt('Device', 'Naprava') }}
                  </p>
                  <p class="text-[10px] text-gray-400">
                    {{ device.lastSeen ? formatTime(device.lastSeen) : tt('Never seen', 'Nikoli videno') }}
                  </p>
                </div>
                <button
                  @click="removeDevice(device.id)"
                  class="opacity-0 group-hover:opacity-100 p-1 rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                  :title="tt('Remove device', 'Odstrani napravo')">
                  <span class="material-symbols-outlined text-sm">delete</span>
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- ══ CHATS TAB ══ -->
        <template v-else-if="activeTab === 'chats'">
          <!-- People list view -->
          <template v-if="chatView === 'list'">
            <div
              class="glass-header px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <div class="relative">
                <span
                  class="absolute left-2.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400"
                  style="font-size: 15px"
                  >search</span
                >
                <input
                  v-model="chatSearch"
                  type="text"
                  :placeholder="tt('Search people...', 'Iskanje oseb...')"
                  class="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <div v-if="peopleStore.loading" class="flex justify-center py-10">
              <div class="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div v-else-if="filteredPeople.length === 0" class="flex flex-col items-center justify-center py-14">
              <span class="material-symbols-outlined text-5xl text-gray-300 dark:text-gray-600">group</span>
              <p class="mt-2 text-sm text-gray-500">{{ tt('No people found', 'Ni najdenih oseb') }}</p>
            </div>
            <div v-else class="flex flex-col">
              <button
                v-for="person in filteredPeople"
                :key="person.userId"
                @click="openConversation(person)"
                class="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left">
                <div class="relative flex-shrink-0">
                  <UserAvatar
                    :user-id="person.avatarImageId ? person.userId : null"
                    :initials="personInitials(personName(person, '?'))"
                    container-class="w-9 h-9 text-xs" />
                  <span
                    class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800"
                    :class="personStatusDot(person.status)"></span>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center justify-between gap-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ personName(person) }}</p>
                    <span
                      v-if="unreadForPerson(person) > 0"
                      class="flex-shrink-0 min-w-[18px] h-4.5 px-1 bg-blue-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {{ unreadForPerson(person) > 9 ? '9+' : unreadForPerson(person) }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {{ lastMessageForPerson(person) || statusLabel(person.status) }}
                  </p>
                </div>
              </button>
            </div>
          </template>

          <!-- Inline conversation view -->
          <template v-else-if="chatView === 'conversation'">
            <!-- Conversation header (sticky) -->
            <div
              class="glass-header flex items-center gap-3 px-3 py-2 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10 flex-shrink-0">
              <button
                @click="backToList()"
                class="p-1.5 -ml-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                <span class="material-symbols-outlined text-lg">arrow_back</span>
              </button>
              <UserAvatar
                :user-id="activePerson?.avatarImageId ? activePerson.userId : null"
                :initials="activePerson ? personInitials(personName(activePerson, '?')) : ''"
                container-class="w-8 h-8 text-xs" />
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {{ personName(activePerson) }}
                </p>
                <p class="text-[10px] text-gray-400">{{ statusLabel(activePerson?.status || 'offline', true) }}</p>
              </div>
              <router-link
                to="/people"
                @click="closeIfFloating()"
                class="p-1.5 rounded-md text-gray-400 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                :title="tt('Open full chat', 'Odpri celoten klepet')">
                <span class="material-symbols-outlined text-sm">open_in_full</span>
              </router-link>
            </div>
            <!-- Messages -->
            <div
              ref="chatMessagesEl"
              class="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-900"
              style="min-height: 0">
              <div v-if="convLoading" class="flex justify-center py-8">
                <div class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <div
                v-else-if="activeMessages.length === 0"
                class="flex flex-col items-center justify-center py-10 text-center">
                <span class="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-600"
                  >chat_bubble_outline</span
                >
                <p class="mt-2 text-xs text-gray-400">
                  {{ tt('No messages yet. Say hi!', 'Ni še sporočil. Pozdravi!') }}
                </p>
              </div>
              <div
                v-for="msg in activeMessages"
                :key="msg.id"
                class="flex"
                :class="msg.senderId === authStore.user?.id ? 'justify-end' : 'justify-start'">
                <div
                  class="max-w-[85%] rounded-2xl px-3 py-2 text-sm"
                  :class="
                    msg.senderId === authStore.user?.id
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white rounded-bl-sm'
                  ">
                  {{ msg.content }}
                </div>
              </div>
            </div>
            <!-- Chat input -->
            <div
              class="glass-header border-t border-gray-200 dark:border-gray-700 px-3 py-2 bg-white dark:bg-gray-800 flex-shrink-0">
              <div class="flex gap-2">
                <input
                  v-model="chatInput"
                  @keydown.enter.prevent="sendChat"
                  type="text"
                  :placeholder="tt('Type a message...', 'Vnesite sporočilo...')"
                  class="flex-1 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500"
                  :disabled="chatSending" />
                <button
                  @click="sendChat"
                  :disabled="!chatInput.trim() || chatSending"
                  class="flex-shrink-0 w-9 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-xl flex items-center justify-center transition-colors">
                  <span
                    v-if="chatSending"
                    class="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                  <span v-else class="material-symbols-outlined text-sm">send</span>
                </button>
              </div>
            </div>
          </template>
        </template>

        <!-- ══ PREFERENCES TAB ══ -->
        <template v-else-if="activeTab === 'preferences'">
          <div class="p-4 space-y-1">
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-3">
              {{ tt('Appearance', 'Videz') }}
            </p>

            <!-- Mode toggle -->
            <div class="flex gap-2 px-1 mb-3">
              <button
                @click="themeStore.setMode('light')"
                class="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors"
                :class="
                  !themeStore.isDark
                    ? 'bg-primary-500/15 text-primary-500 ring-1 ring-primary-500/40'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                ">
                <span class="material-symbols-outlined text-base">light_mode</span> {{ tt('Light', 'Svetlo') }}
              </button>
              <button
                @click="themeStore.setMode('dark')"
                class="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-colors"
                :class="
                  themeStore.isDark
                    ? 'bg-primary-500/15 text-primary-500 ring-1 ring-primary-500/40'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                ">
                <span class="material-symbols-outlined text-base">dark_mode</span> {{ tt('Dark', 'Temno') }}
              </button>
            </div>

            <!-- Color picker -->
            <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-2 mb-2">
              {{ tt('Color', 'Barva') }}
            </p>
            <div class="px-1">
              <div class="flex gap-2 flex-wrap justify-center">
                <button
                  v-for="c in availableColors"
                  :key="c"
                  @click="themeStore.setColor(c)"
                  class="flex flex-col items-center gap-1 py-2 px-1 rounded-lg transition-colors min-w-[3rem]"
                  :class="
                    themeStore.color === c
                      ? 'bg-primary-500/15 ring-1 ring-primary-500/40'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  ">
                  <span
                    class="w-7 h-7 rounded-full border-2 flex-shrink-0"
                    :class="themeStore.isDark ? COLOR_INFO[c].darkSwatch : COLOR_INFO[c].lightSwatch"></span>
                  <span
                    class="text-[10px] font-medium"
                    :class="themeStore.color === c ? 'text-primary-400' : 'text-gray-500 dark:text-gray-400'"
                    >{{ tt(COLOR_INFO[c].label.en, COLOR_INFO[c].label.sl) }}</span
                  >
                </button>
              </div>
            </div>

            <!-- Custom hue slider -->
            <div v-if="themeStore.color === 'custom'" class="px-2 mt-3">
              <label
                class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1 block"
                >{{ tt('Custom Hue', 'Barvni odtenek') }}</label
              >
              <input
                type="range"
                min="0"
                max="360"
                :value="themeStore.customHue"
                @input="themeStore.setCustomHue(Number(($event.target as HTMLInputElement).value))"
                class="w-full h-2 rounded-full appearance-none cursor-pointer"
                style="
                  background: linear-gradient(
                    to right,
                    hsl(0, 70%, 50%),
                    hsl(60, 70%, 50%),
                    hsl(120, 70%, 50%),
                    hsl(180, 70%, 50%),
                    hsl(240, 70%, 50%),
                    hsl(300, 70%, 50%),
                    hsl(360, 70%, 50%)
                  );
                " />
              <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 text-center">{{ themeStore.customHue }}°</p>
            </div>

            <!-- Shape preset (corners + spacing) + glass blur switch -->
            <p
              class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-2 mb-2 mt-4">
              {{ tt('Shape & glass', 'Oblika in steklo') }}
            </p>
            <div class="px-1">
              <RoundnessPicker />
            </div>
            <!-- Card style (normal | glass), glass background + tint -->
            <div class="px-1 mt-2">
              <GlassStylePicker />
            </div>

            <div class="glass-divider border-t border-gray-100 dark:border-gray-700 my-2" />
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-3 pt-1">
              {{ tt('Notifications', 'Obvestila') }}
            </p>

            <!-- Mute notifications -->
            <button
              class="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              @click="toggleMute()">
              <div
                class="p-2 rounded-lg flex-shrink-0"
                :class="muted ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-100 dark:bg-gray-700'">
                <span class="material-symbols-outlined text-base" :class="muted ? 'text-red-500' : ''">
                  {{ muted ? 'notifications_off' : 'notifications' }}
                </span>
              </div>
              <div class="flex-1 text-left">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ muted ? tt('Notifications muted', 'Obvestila utišana') : tt('Notifications', 'Obvestila') }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    muted
                      ? tt('All notifications silenced', 'Vsa obvestila utišana')
                      : tt('Sound & alerts enabled', 'Zvok in opozorila omogočeni')
                  }}
                </p>
              </div>
              <span class="flex-shrink-0">
                <span
                  :class="[
                    'inline-flex items-center w-10 h-5.5 rounded-full transition-colors',
                    muted ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600',
                  ]">
                  <span
                    :class="[
                      'w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5',
                      muted ? 'translate-x-5' : 'translate-x-0',
                    ]"></span>
                </span>
              </span>
            </button>

            <!-- Interface. Both the section and its one switch belong to a
                 viewer's hidden list (card t_803e86a4) — the float they toggle
                 is never offered to a guest. -->
            <template v-if="!isViewer">
              <div class="glass-divider border-t border-gray-100 dark:border-gray-700 my-2" />
              <p
                class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-3 pt-1">
                {{ tt('Interface', 'Vmesnik') }}
              </p>

              <!-- Show Agent FAB -->
              <button
                class="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                @click="ui.toggleShowAgentFab()">
              <div class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <span class="material-symbols-outlined text-base">smart_toy</span>
              </div>
              <div class="flex-1 text-left">
                <p class="text-sm font-medium text-gray-900 dark:text-white">Agent FAB</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    ui.showAgentFab
                      ? tt('Floating button visible', 'Plavajoči gumb viden')
                      : tt('Floating button hidden', 'Plavajoči gumb skrit')
                  }}
                </p>
              </div>
              <span class="flex-shrink-0">
                <span
                  :class="[
                    'inline-flex items-center w-10 h-5.5 rounded-full transition-colors',
                    ui.showAgentFab ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600',
                  ]">
                  <span
                    :class="[
                      'w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5',
                      ui.showAgentFab ? 'translate-x-5' : 'translate-x-0',
                    ]"></span>
                </span>
              </span>
            </button>

            <!-- Show Demo FAB (card D2, plan §4.8): the same preference shape as
                 the agent's, offered only when the hub actually is a demo — a row
                 that toggles nothing on a normal hub would be a dead switch. -->
            <button
              v-if="demoModeEnabled"
              data-testid="user-drawer-demo-fab"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              @click="ui.toggleShowDemoFab()">
              <div class="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                <span class="material-symbols-outlined text-base">play_circle</span>
              </div>
              <div class="flex-1 text-left">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ tt('Demo player', 'Demo predvajalnik') }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    ui.showDemoFab
                      ? tt('Floating clock visible', 'Plavajoča ura vidna')
                      : tt('Floating clock hidden', 'Plavajoča ura skrita')
                  }}
                </p>
              </div>
              <span class="flex-shrink-0">
                <span
                  :class="[
                    'inline-flex items-center w-10 h-5.5 rounded-full transition-colors',
                    ui.showDemoFab ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600',
                  ]">
                  <span
                    :class="[
                      'w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5',
                      ui.showDemoFab ? 'translate-x-5' : 'translate-x-0',
                    ]"></span>
                </span>
              </span>
            </button>
            </template>

            <div class="glass-divider border-t border-gray-100 dark:border-gray-700 my-2" />
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide px-1 mb-3 pt-1">
              {{ tt('Voice', 'Glas') }}
            </p>

            <!-- Wake Word Detection -->
            <button
              class="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              @click="toggleWakeWord()">
              <div
                class="p-2 rounded-lg flex-shrink-0"
                :class="
                  agentStore.wakeWordEnabled ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-gray-700'
                ">
                <span
                  class="material-symbols-outlined text-base"
                  :class="agentStore.wakeWordEnabled ? 'text-purple-500' : ''">
                  {{ agentStore.wakeWordEnabled ? 'mic' : 'mic_off' }}
                </span>
              </div>
              <div class="flex-1 text-left">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ tt('Wake Word', 'Aktivacijska beseda') }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  {{
                    agentStore.wakeWordEnabled
                      ? `${tt('Say', 'Izgovorite')} "${heyAgent}" ${tt('to activate', 'za aktivacijo')}`
                      : tt('Voice activation disabled', 'Glasovna aktivacija onemogočena')
                  }}
                </p>
              </div>
              <span class="flex-shrink-0">
                <span
                  :class="[
                    'inline-flex items-center w-10 h-5.5 rounded-full transition-colors',
                    agentStore.wakeWordEnabled ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600',
                  ]">
                  <span
                    :class="[
                      'w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5',
                      agentStore.wakeWordEnabled ? 'translate-x-5' : 'translate-x-0',
                    ]"></span>
                </span>
              </span>
            </button>
          </div>
        </template>
      </div>

      <!-- ── Footer: Logout (User tab only) ────────────────────────────────── -->
      <div v-if="activeTab === 'user'" class="glass-divider border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
        <button
          class="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          @click="logout()">
          <span class="material-symbols-outlined text-base">logout</span>
          {{ tt('Logout', 'Odjava') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

import { heyAgent } from '../config/brand';
import { useUIStore, type UserDrawerTab } from '../stores/ui';
import { useAuthStore } from '../stores/auth';
import { useThemeStore, LIGHT_COLORS, DARK_COLORS, COLOR_INFO } from '../stores/theme';
import { tt } from '../utils/consoleText';
import { formatDate } from '../utils/formatDate';
import RoundnessPicker from './RoundnessPicker.vue';
import GlassStylePicker from './GlassStylePicker.vue';
import { usePeopleStore, type User } from '../stores/people';
import { personName, personInitials } from '../utils/personName';
import { useAlarmsStore } from '../stores/alarms';
import { useTimersStore } from '../stores/timers';
import { useRemindersStore } from '../stores/reminders';
import { useAgentStore } from '../stores/agent';
import { apiFetch } from '../services/api';

import { useUserAvatar } from '../composables/useUserAvatar';
import { useDemoGuest } from '../composables/useDemoGuest';
import { useDemoClock } from '../composables/useDemoClock';
import UserAvatar from './UserAvatar.vue';

const ui = useUIStore();
const authStore = useAuthStore();
/**
 * The Demo-player row exists only on a demo hub (card D2): `modeEnabled` is the
 * hub's own `demo_mode.enabled`, shared with the FAB through the clock's mirror,
 * so the row appears exactly when the float it toggles can.
 */
const demoClock = useDemoClock();
const demoModeEnabled = computed(() => demoClock.modeEnabled.value);
const themeStore = useThemeStore();
const peopleStore = usePeopleStore();
const alarmsStore = useAlarmsStore();
const timersStore = useTimersStore();
const remindersStore = useRemindersStore();
const agentStore = useAgentStore();
/**
 * The viewer (guest) doors this drawer must not offer (card `t_803e86a4`): the
 * account centre behind `/account` and the agent float's toggle — both are
 * operator/account surfaces, and a demo guest holds a throwaway hub row with no
 * cloud account behind it. Everything else in this drawer (theme, sound, the
 * person's own alarms/timers/reminders) is theirs.
 */
const { isViewer } = useDemoGuest();

const availableColors = computed(() => (themeStore.isDark ? DARK_COLORS : LIGHT_COLORS));

// ── Responsive detection ──────────────────────────────────────────────────────
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth < 768);
function onResize() {
  isMobile.value = window.innerWidth < 768;
}
onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

// ── Open state ────────────────────────────────────────────────────────────────
const open = computed(() => ui.userDrawerOpen);
const effectivePinned = computed(() => !isMobile.value && ui.userDrawerPinned);

const panelStyle = computed(() => {
  if (effectivePinned.value) return { width: '380px' };
  if (isMobile.value) return { top: '0', left: '0', right: '0', bottom: '0' };
  return { width: '380px', right: '16px', top: '60px', bottom: '16px' };
});

function close() {
  ui.closeUserDrawer();
}

/** A link tap keeps a pinned drawer in place; only a floating drawer leaves. */
function closeIfFloating() {
  if (effectivePinned.value) return;
  close();
}

// ── Tab state ────────────────────────────────────────────────────────────────
const activeTab = computed({
  get: () => ui.userDrawerTab,
  set: (v: UserDrawerTab) => {
    ui.userDrawerTab = v;
  },
});

const TABS = computed(() => [
  { id: 'user' as UserDrawerTab, label: { en: 'Me', sl: 'Jaz' }, icon: 'person', badge: 0, badgeColor: '' },
  {
    id: 'chats' as UserDrawerTab,
    label: { en: 'Chats', sl: 'Klepet' },
    icon: 'chat_bubble',
    badge: peopleStore.unreadTotal,
    badgeColor: 'bg-blue-500',
  },
  {
    id: 'preferences' as UserDrawerTab,
    label: { en: 'Prefs', sl: 'Nastavitve' },
    icon: 'tune',
    badge: 0,
    badgeColor: '',
  },
]);

// ── User info ─────────────────────────────────────────────────────────────────
// Hub avatar resolved through the routed API layer (works in local, cloud WebRTC
// and Electron), with a fallback to the cloud account image in cloud mode.
const { url: userImageUrl, error: avatarError } = useUserAvatar();
const userInitials = computed(() => {
  if (authStore.user?.name)
    return authStore.user.name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  return authStore.user?.email?.substring(0, 2).toUpperCase() || 'U';
});

// ── Status ───────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'online', label: { en: 'Online', sl: 'Na spletu' }, color: 'text-green-500' },
  { value: 'away', label: { en: 'Away', sl: 'Odsoten' }, color: 'text-yellow-500' },
  { value: 'dnd', label: { en: 'Do Not Disturb', sl: 'Ne moti' }, color: 'text-red-500' },
  { value: 'offline', label: { en: 'Offline', sl: 'Brez povezave' }, color: 'text-gray-400' },
];

/**
 * The presence words the hub keeps as enum values (`online`, `away`, `dnd`,
 * `offline`). The English half is the raw word the drawer printed before this
 * table existed (the header capitalises it with CSS), the Slovene half words
 * it for the reader; an unknown status is returned exactly as it came.
 */
const STATUS_WORDS: Record<string, [string, string]> = {
  online: ['online', 'na spletu'],
  away: ['away', 'odsoten'],
  dnd: ['dnd', 'ne moti'],
  offline: ['offline', 'brez povezave'],
};
/**
 * Slovene agrees with the count (1 omogočen, 2 omogočena, 3 omogočeni,
 * 5 omogočenih); the last two digits decide, as the language does.
 */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}
/**
 * The same word with its first letter uppercased — the drawer used to do this
 * with CSS `text-transform: capitalize`, which capitalises *every* word and so
 * turned the Slovene 'na spletu' into 'Na Spletu'.
 */
function statusLabel(status?: string | null, capitalise = false): string {
  const pair = STATUS_WORDS[(status || '').toLowerCase()];
  const word = pair ? tt(pair[0], pair[1]) : status || '';
  return capitalise ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}
const myStatus = computed({
  get: (): string => peopleStore.myStatus || 'online',
  set: (val: string) => peopleStore.setMyStatus(val as 'online' | 'away' | 'dnd' | 'offline'),
});
const statusDotColor = computed(() => {
  const s = peopleStore.myStatus;
  if (s === 'online') return 'bg-green-500';
  if (s === 'away') return 'bg-yellow-500';
  if (s === 'dnd') return 'bg-red-500';
  return 'bg-gray-400';
});

// ── Hub-aware navigation paths ────────────────────────────────────────────────
function buildHubPath(tail: string): string {
  // Build hub-scoped or standalone path based on current URL
  const m = window.location.hash
    ? window.location.hash.match(/#\/h\/([^/]+)/)
    : window.location.pathname.match(/\/h\/([^/]+)/);
  return m ? `/h/${m[1]}/${tail}` : `/${tail}`;
}
const hubAlarmsPath = computed(() => buildHubPath('alarms'));
const hubTimersPath = computed(() => buildHubPath('timers'));
const hubRemindersPath = computed(() => buildHubPath('reminders'));

/** Chat, Photos, Calendar, Notes and Lists left the main menu — they live here now. */
const pageShortcuts = computed(() => [
  { id: 'chat', icon: 'chat_bubble', label: { en: 'Chat', sl: 'Klepet' }, to: buildHubPath('people') },
  { id: 'photos', icon: 'photo_library', label: { en: 'Photos', sl: 'Fotografije' }, to: buildHubPath('gallery') },
  {
    id: 'calendar',
    icon: 'calendar_month',
    label: { en: 'Calendar', sl: 'Koledar' },
    to: buildHubPath('calendar'),
  },
  { id: 'notes', icon: 'description', label: { en: 'Notes', sl: 'Opombe' }, to: buildHubPath('notes') },
  { id: 'lists', icon: 'shopping_cart', label: { en: 'Lists', sl: 'Seznami' }, to: buildHubPath('shopping') },
]);

// ── Alarm / Timer / Reminder counters ────────────────────────────────────────
const enabledAlarmsCount = computed(() => alarmsStore.alarms.filter((a) => a.enabled).length);
const runningTimersCount = computed(() => timersStore.timers.filter((t) => t.status === 'running').length);
const upcomingRemindersCount = computed(
  () => remindersStore.reminders.filter((r) => !r.completed && new Date(r.remindAt) > new Date()).length,
);

// ── Mute notifications ────────────────────────────────────────────────────────
const muted = ref(localStorage.getItem('notifications-muted') === 'true');
function toggleMute() {
  muted.value = !muted.value;
  localStorage.setItem('notifications-muted', String(muted.value));
}

// -- Wake Word -----------------------------------------------------------------
function toggleWakeWord() {
  if (agentStore.wakeWordEnabled) {
    agentStore.disableWakeWord();
  } else {
    agentStore.enableWakeWord();
  }
}

/** One row of the signed-in user's device list, as this drawer renders it. */
interface UserDeviceRow {
  id: string;
  deviceName?: string | null;
  platform?: string | null;
  lastSeen?: string | null;
}

const devices = ref<UserDeviceRow[]>([]);
const devicesLoading = ref(false);

async function loadDevices() {
  if (!authStore.user?.id) return;
  devicesLoading.value = true;
  try {
    devices.value = await apiFetch(`/users/${authStore.user.id}/devices`);
  } catch {
    devices.value = [];
  } finally {
    devicesLoading.value = false;
  }
}

async function removeDevice(deviceId: string) {
  if (!authStore.user?.id) return;
  try {
    await apiFetch(`/users/${authStore.user.id}/devices/${deviceId}`, { method: 'DELETE' });
    devices.value = devices.value.filter((d) => d.id !== deviceId);
  } catch {
    /* best effort — the row is gone locally either way */
  }
}

// ── Chats tab ─────────────────────────────────────────────────────────────────
const chatSearch = ref('');
type ChatView = 'list' | 'conversation';
const chatView = ref<ChatView>('list');
const activePerson = ref<User | null>(null);
const activeConvId = ref<string | null>(null);
const convLoading = ref(false);
const chatInput = ref('');
const chatSending = ref(false);
const chatMessagesEl = ref<HTMLElement | null>(null);

const filteredPeople = computed(() => {
  const q = chatSearch.value.toLowerCase().trim();
  if (!q) return peopleStore.people;
  return peopleStore.people.filter((p) => personName(p).toLowerCase().includes(q));
});
const activeMessages = computed(() => {
  if (!activeConvId.value) return [];
  return peopleStore.messages.get(activeConvId.value) || [];
});

function personStatusDot(status?: string) {
  if (status === 'online') return 'bg-green-500';
  if (status === 'away') return 'bg-yellow-500';
  if (status === 'dnd') return 'bg-red-500';
  return 'bg-gray-400';
}
function unreadForPerson(person: User) {
  const conv = peopleStore.conversations.find(
    (c) => c.type === 'direct' && c.participants.some((p) => p.userId === person.userId),
  );
  return conv?.unreadCount || 0;
}
function lastMessageForPerson(person: User) {
  const conv = peopleStore.conversations.find(
    (c) => c.type === 'direct' && c.participants.some((p) => p.userId === person.userId),
  );
  return conv?.lastMessage?.content || '';
}

async function openConversation(person: User) {
  activePerson.value = person;
  chatView.value = 'conversation';
  convLoading.value = true;
  try {
    let conv = peopleStore.conversations.find(
      (c) => c.type === 'direct' && c.participants.some((p) => p.userId === person.userId),
    );
    if (!conv) {
      conv = (await peopleStore.createConversation('direct', { participantIds: [person.userId] })) ?? undefined;
    }
    if (conv) {
      activeConvId.value = conv.id;
      await peopleStore.fetchMessages(conv.id);
    }
  } finally {
    convLoading.value = false;
    nextTick(() => {
      if (chatMessagesEl.value) chatMessagesEl.value.scrollTop = chatMessagesEl.value.scrollHeight;
    });
  }
}

function backToList() {
  chatView.value = 'list';
  activePerson.value = null;
  activeConvId.value = null;
}

watch(activeMessages, () => {
  nextTick(() => {
    if (chatMessagesEl.value) chatMessagesEl.value.scrollTop = chatMessagesEl.value.scrollHeight;
  });
});

async function sendChat() {
  if (!chatInput.value.trim() || chatSending.value || !activeConvId.value) return;
  chatSending.value = true;
  const text = chatInput.value.trim();
  chatInput.value = '';
  try {
    await peopleStore.sendMessage(activeConvId.value, text);
  } finally {
    chatSending.value = false;
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
watch(open, async (isOpen) => {
  if (!isOpen) return;
  agentStore.initialize();
  if (peopleStore.people.length === 0) peopleStore.fetchPeople();
  if (peopleStore.conversations.length === 0) peopleStore.fetchConversations();
  loadDevices();
  if (alarmsStore.alarms.length === 0) alarmsStore.fetchAlarms();
  if (timersStore.timers.length === 0) timersStore.fetchTimers();
  if (remindersStore.reminders.length === 0) remindersStore.fetchReminders();
});

// Open chat with specific person (from external trigger)
watch(
  () => ui.chatOpenPersonId,
  async (userId) => {
    if (!userId) return;
    ui.chatOpenPersonId = null;
    if (peopleStore.people.length === 0) await peopleStore.fetchPeople();
    const person = peopleStore.people.find((p) => p.userId === userId);
    if (person) openConversation(person);
  },
);

// ── Logout ────────────────────────────────────────────────────────────────────
function logout() {
  // Close the hub connection (WebRTC peer + signaling socket + socket.io)
  // before clearing auth state — otherwise a cloud (WebRTC) session would stay
  // alive after "Logout". Cloud account tokens are intentionally kept so the
  // user can reconnect with one click.
  authStore.disconnectAll();
  // The person signed out: every open tab goes and the launcher is left
  // (`closeTabs`), instead of only this tab (`leaveHubToLauncher`).
  authStore.logout({ closeTabs: true });
  close();
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function formatTime(dateStr: string) {
  const date = new Date(dateStr);
  const m = Math.floor((Date.now() - date.getTime()) / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1) return tt('Just now', 'Pravkar');
  if (m < 60) return `${m}${tt('m ago', ' min nazaj')}`;
  if (h < 24) return `${h}${tt('h ago', ' h nazaj')}`;
  if (d < 7) return `${d}${tt('d ago', ' dni nazaj')}`;
  return formatDate(date, { month: 'short', day: 'numeric' });
}

onMounted(() => {
  // UserDrawer mounted
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Pinned: push content (width animation) */
.drawer-push-enter-active,
.drawer-push-leave-active {
  transition:
    width 0.22s ease,
    opacity 0.15s ease;
  overflow: hidden;
}
.drawer-push-enter-from,
.drawer-push-leave-to {
  width: 0 !important;
  opacity: 0;
}

/* Floating/mobile: slide from right */
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition:
    transform 0.22s ease,
    opacity 0.15s ease;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
